import { useId, useRef } from 'react';
import {
  Button,
  ProgressBar,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useFloatingPanelStyles } from './floatingPanelStyles';

/**
 * SavingDraftOverlay — Stage 1 of the multi-step "Create new workflow" flow.
 *
 * Centered floating panel with a subtle backdrop that informs the user we're
 * persisting the current workflow's draft before showing the name dialog.
 *
 * Two variants:
 *
 *   - "saving"  ➜ headline + brand-tinted indeterminate progress bar. No
 *                  buttons (the save itself cannot be cancelled mid-flight).
 *                  Renders as a passive `role="status"` live region — the
 *                  backdrop dims the canvas but no focus trap (transient,
 *                  no actionable controls).
 *   - "error"   ➜ headline, error description, and three recovery buttons:
 *                  Try again (primary) · Continue anyway · Cancel. Promoted
 *                  to a real modal: `role="alertdialog"` + `aria-modal="true"`
 *                  + focus trap on the panel, so keyboard users can't Tab
 *                  out into the canvas behind the dim while a decision is
 *                  pending.
 *
 * Why a custom panel instead of `<Dialog>`:
 *   Fluent's Dialog chrome is heavier than this surface needs (the saving
 *   variant in particular is transient and non-actionable). The shared
 *   `useFloatingPanelStyles` gives us the centered overlay + backdrop +
 *   concentric radii without the dialog header/footer baggage.
 */

const useStyles = makeStyles({
  /**
   * Brand-tinted indeterminate bar. Fluent's `ProgressBar value={undefined}`
   * draws a moving fill that already animates; we re-tint the fill via
   * the available CSS variable so it picks up the brand accent without
   * introducing a custom <keyframes>.
   *
   * TODO: replace with a native Fluent appearance (e.g. `appearance="brand"`)
   * once one ships — this depends on Fluent's internal variable name and
   * will break silently if Fluent restructures it.
   */
  progress: {
    width: '100%',
    '--colorCompoundBrandBackground': tokens.colorBrandBackground,
  },
  /** Reserve consistent height so the layout doesn't shift between variants. */
  progressTrack: {
    minHeight: '4px',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export interface SavingDraftOverlayProps {
  /**
   * `saving` shows the indeterminate progress bar; `error` shows the
   * recovery copy and three action buttons. The parent owns transitions
   * between variants.
   */
  variant: 'saving' | 'error';
  /** Error message from the failed save (only used in `error` variant). */
  errorMessage?: string | null;
  /** Invoked when the user clicks "Try again" in the error variant. */
  onRetry?: () => void;
  /**
   * Invoked when the user clicks "Continue anyway" in the error variant.
   * Advances the flow to naming without persisting the dirty draft.
   */
  onContinue?: () => void;
  /** Invoked when the user clicks "Cancel" — closes the entire flow. */
  onCancel?: () => void;
}

export const SavingDraftOverlay = ({
  variant,
  errorMessage,
  onRetry,
  onContinue,
  onCancel,
}: SavingDraftOverlayProps) => {
  const styles = useFloatingPanelStyles();
  const ownStyles = useStyles();
  const titleId = useId();
  const descriptionId = useId();
  // Focus-trap refs. The trap is only active in the error variant (which
  // owns three real buttons the user must choose between). The saving
  // variant is a passive `role="status"` live region — no buttons, no
  // trap needed, and the next phase (naming or error) will take over
  // focus when this surface unmounts.
  const panelRef = useRef<HTMLDivElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement | null>(null);

  const isError = variant === 'error';

  useFocusTrap({
    active: isError,
    containerRef: panelRef,
    initialFocusRef: retryButtonRef,
  });

  return (
    <div className={styles.viewport} data-testid="saving-draft-overlay">
      <div
        ref={panelRef}
        className={styles.panel}
        role={isError ? 'alertdialog' : 'status'}
        aria-live={isError ? undefined : 'polite'}
        aria-modal={isError ? 'true' : undefined}
        aria-labelledby={titleId}
        aria-describedby={isError ? descriptionId : undefined}
      >
        {isError ? (
          <>
            <Text id={titleId} as="h2" className={styles.title}>
              Couldn&rsquo;t save your draft
            </Text>
            <Text id={descriptionId} as="p" className={styles.body}>
              {errorMessage
                ? `Saving the current workflow failed: ${errorMessage}. You can retry, continue without saving, or cancel creating a new workflow.`
                : 'Saving the current workflow failed. You can retry, continue without saving, or cancel creating a new workflow.'}
            </Text>
            <div className={styles.actions}>
              <Button appearance="subtle" onClick={onCancel}>
                Cancel
              </Button>
              <Button appearance="secondary" onClick={onContinue}>
                Continue anyway
              </Button>
              <Button
                ref={(el: HTMLButtonElement | null) => {
                  retryButtonRef.current = el;
                }}
                appearance="primary"
                onClick={onRetry}
              >
                Try again
              </Button>
            </div>
          </>
        ) : (
          <>
            <Text id={titleId} as="h2" className={styles.title}>
              Saving your draft&hellip;
            </Text>
            <ProgressBar
              shape="rounded"
              thickness="medium"
              className={mergeClasses(ownStyles.progress, ownStyles.progressTrack)}
              aria-label="Saving current workflow draft"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SavingDraftOverlay;
